import { Auth } from "@aws-amplify/auth";
import { API, Hub, Logger } from "aws-amplify";
import { apiGateway } from "../../config";
import { externalNavigate } from "./navigation";
import { checkOnlineStatus, clearObjectStore, formatUSPhoneNumber, generateMnemonic, getCurrentTimezone, passkeyAuth } from "../../utilities";
import {
  CLEAR_ALL,
  IS_LOGGED_IN,
  IS_LOGGED_OUT,
  LOGGING_OUT,
  SET_LOCKED,
  SET_TOKEN,
  UPDATE_CREDENTIALS,
  AUTO_VERIFYING_PHONE,
  SET_ABLY_TOKEN,
  SET_SESSION_START_TIME
} from "./constants";
import { showLoader, hideLoader } from "./loader";
import { checkEmail, createUser, getUser } from "./user";
import { createAccount, getAccounts } from "./accounts";
import { store, history } from "..";
import { showNotification } from "./notification";
import debug from "../../utilities/debug";
import { toastr } from "react-redux-toastr";
import LogRocket from "logrocket";
import { themeBuilder } from "../../global-styles";
import { getProjects } from "./projects";
import config from "../../config";
import { Crisp } from "crisp-sdk-web";
import { createStripeCustomer, createSubscription } from "./stripe";
import { getPlans } from "./plans";
import { updateChatBadge } from "./session";
import { buildAvatarUrl } from '../../utilities/avatarCache';
// New: centralized auth challenge state (replaces sessionStorage pendingMFA / pendingForcePwd flags)
import { SET_AUTH_CHALLENGE } from "./constants";
const Gateway = apiGateway.find((gateway) => gateway.name === "client");
const logger = new Logger("Auth-Logger");

// Guards to prevent race between hosted UI logout redirect and local bootstrap re-auth
let __logoutInProgress = false;
let __localSignOutCompleted = false;
let __suppressNextBootstrap = false;
let __passkeyBootstrapDone = false;

// Shared provisioning helper used by confirmSignup and federated (Google) first-login flows.
// Mirrors the inner portion of confirmSignup (user + account + stripe + subscription + user refetch + accounts fetch + analytics + redirect).
// params: {
//   cognitoUser, email, first_name, last_name, phone, referral, partner, company_name, invite, debugSource, avatar, npi_number, npi_data, npi_looked_up_at
// }
const provisionInitialSession = async (params) => {
  const {
    cognitoUser,
    email,
    first_name,
    last_name,
    phone = null,
    referral = null,
    partner = false,
    company_name = null,
    invite = null,
    debugSource = 'unknown',
    avatar = null,
    npi_number = null,
    npi_data = null,
    npi_looked_up_at = null
  } = params || {};
  const dispatch = store.dispatch;
  const log = (...a) => debug.log('authentication', `[PROVISION] [${debugSource}]`, ...a);
  try {
    log('start');
    // Create user (prefer stable UUID sub over federated provider username like Google_*)
    const canonicalId = cognitoUser?.attributes?.sub || cognitoUser.username;
    const newUserPayload = {
      cognito_id: canonicalId,
      username: generateMnemonic(),
      email,
      phone: phone ? formatUSPhoneNumber(phone) : null,
      first_name,
      last_name,
      time_zone: getCurrentTimezone()
    };
    if (avatar) newUserPayload.avatar = avatar; // only set on initial create
    let createUserResult = await createUser(newUserPayload, canonicalId, referral, partner, company_name);

    if (!createUserResult.success) {
      const msg = createUserResult.message || '';
      const conflict = /exist/i.test(msg);
      if (conflict) {
        log('user exists, proceeding');
      } else {
        log('createUser failed', createUserResult);
        try { dispatch(showNotification('error', 'Setup Incomplete', 'We could not finish creating your account (code: createUser). Please retry sign in.')); } catch(_) {}
        return { success: false, stage: 'createUser', error: createUserResult, notified: true };
      }
    }
  const userId = canonicalId;
    let createAccountResult = await createAccount({
      account_id: userId,
      cognito_id: userId,
      partner: partner || false,
      company_name: company_name,
      referrer: referral || null,
      account_name: `${first_name} ${last_name}`.trim(),
      npi_number: npi_number,
      npi_data: npi_data,
      npi_looked_up_at: npi_looked_up_at
    }, invite);
    if (!createAccountResult.success) {
      const msg = createAccountResult.message || '';
      const code = createAccountResult.code || '';
      const conflict = /exist/i.test(msg);
      
      // Handle NPI duplicate specifically
      if (code === 'NPI_ALREADY_EXISTS') {
        log('NPI already registered', createAccountResult);
        try { dispatch(showNotification('error', 'NPI Already Registered', createAccountResult.message)); } catch(_) {}
        return { success: false, stage: 'createAccount', error: createAccountResult, notified: true };
      }
      
      if (conflict) {
        log('account exists, proceeding');
      } else {
        log('createAccount failed', createAccountResult);
        try { dispatch(showNotification('error', 'Setup Incomplete', 'We hit an issue initializing your workspace (code: createAccount). Please retry sign in.')); } catch(_) {}
        return { success: false, stage: 'createAccount', error: createAccountResult, notified: true };
      }
    }
    const accountId = createAccountResult?.payload?.account_id || userId;
    // Small settle delay to allow DB commit & downstream triggers (Hubspot, etc.) to finish before first membership read
    await new Promise(r => setTimeout(r, 120));
    // Fetch accounts with more resilient retry (eventual consistency / replication / cold start latency)
    let accounts = null;
    const maxAccountAttempts = 7; // was 4
    for (let attempt = 1; attempt <= maxAccountAttempts; attempt++) {
      accounts = await dispatch(getAccounts(accountId, userId, true));
      const list = accounts && (accounts.accounts || accounts.payload);
      if (Array.isArray(list) && list.length) {
        if (!accounts.accounts && accounts.payload) accounts = { ...accounts, accounts: accounts.payload }; // normalize
        debug.log('authentication', '[PROVISION] accounts fetched', { attempt, len: list.length });
        break;
      }
      debug.log('authentication', '[PROVISION] accounts retry', { attempt, accountId, userId, success: accounts?.success, msg: accounts?.message });
      if (attempt < maxAccountAttempts) {
        const sleep = 180 * attempt; // ~1.1s total over 6 waits (180,360,540,720,900,1080)
        await new Promise(r => setTimeout(r, sleep));
      }
    }
    if (!accounts || !Array.isArray(accounts.accounts) || !accounts.accounts.length) {
      log('accounts fetch failed after retries');
      try { dispatch(showNotification('error', 'Setup Delayed', 'We could not verify your account membership (code: accountsFetchRetry). Please retry sign in.')); } catch(_) {}
      return { success: false, stage: 'accountsFetchRetry', notified: true };
    }

    // Initial user fetch (after accounts membership established)
    await getUser(userId, accounts.current?.account_id || accountId, true);

    // Stripe customer + subscription (requires account + membership for permission)
    try {
      const existingUser = store.getState().authentication?.user;
      const existingCustomerId = existingUser?.customer_id;
      let customer = null;
      if (!existingCustomerId) {
        customer = await dispatch(createStripeCustomer(accounts.current.account_id, userId, {
          phone: phone ? formatUSPhoneNumber(phone) : null,
          name: `${first_name} ${last_name}`.trim(),
          email,
          address: { country: 'US' }
        }));
      }
      if ((customer && customer.id) || existingCustomerId) {
        const plans = await dispatch(getPlans(true, accounts.current.account_id));
        if (Array.isArray(plans)) {
          const freePlan = plans.find(p => !p.value);
          if (freePlan) {
            // Ensure user row reflects customer_id before creating subscription
            const ensuredCustomerId = existingCustomerId || await waitForCustomerId(userId, accounts.current.account_id, 6, 300) || (customer && customer.id);
            await dispatch(createSubscription({}, freePlan.product_id, ensuredCustomerId));
            await getUser(userId, accounts.current.account_id, true); // refresh after subscription effects
          }
        }
      }
    } catch (stripeErr) { log('stripe non-fatal', stripeErr); }

    dispatch({ type: IS_LOGGED_IN });
  // Ensure lock screen cleared immediately after provisioning
  try { dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } }); } catch(_) {}
    const u = store.getState().authentication.user;
    try {
      LogRocket.identify(u.cognito_id, {
        name: `${u.first_name} ${u.last_name}`.trim(),
        email: u.email,
        partner: accounts.current?.partner,
        account_id: accounts.current?.account_id,
        environment: process.env.REACT_APP_STAGE || 'development'
      });
    } catch(_) {}
    setCrisp({ ...u, partner: accounts.current?.partner, company_name: accounts.current?.company_name });
    dispatch(getProjects(true));
    checkOnlineStatus();

    // Removed automatic post-bootstrap redirect to prevent unintended navigation (handled by CoreProvider root route)
    log('complete');
    return { success: true, accounts, user: u };
  } catch (err) {
    log('fatal', err);
    try { dispatch(showNotification('error', 'Setup Failed', 'Unexpected error during sign in. Please retry.')); } catch(_) {}
    return { success: false, stage: 'exception', error: err, notified: true };
  }
};

// Unified bootstrap for any successful Cognito session (email/password, confirmSignup continuation, or federated/hosted UI)
export const bootstrapSession = (options = {}) => async (dispatch) => {
  if (__logoutInProgress) {
    return { success: false, message: 'logout-in-progress' };
  }
  const { provisioningHint = 'Authorizing...', detailedReturn = false } = options;
  dispatch(showLoader(provisioningHint));
  const log = (...args) => debug.log('authentication', '[BOOTSTRAP]', ...args);
  log('start');
  try {
  const cognitoUser = await Auth.currentAuthenticatedUser();
  // Canonical identity (prefer stable UUID sub over provider-prefixed username)
  const canonicalId = cognitoUser?.attributes?.sub || cognitoUser?.username;
  log('authenticated user', { username: cognitoUser?.username, canonicalId });
    // Removed premature IS_LOGGED_IN dispatch (will set once stable state achieved)
    // autoReauth reference no longer needed here; removal prevents unused var warning
    if (!cognitoUser?.signInUserSession) throw new Error('No active session');
    // Mirror signIn challenge handling so federated path surfaces MFA / force-new-password consistently
    if (cognitoUser?.challengeName === 'SMS_MFA' || cognitoUser?.challengeName === 'SOFTWARE_TOKEN_MFA') {
      log('challengeIntercept', { challenge: cognitoUser.challengeName });
      // For challenges we still store username (Cognito API expects it) but downstream API calls will use canonicalId
      try { dispatch({ type: UPDATE_CREDENTIALS, payload: { username: cognitoUser.username, preferredMFA: cognitoUser.preferredMFA, attributes: cognitoUser.attributes } }); } catch(_) {}
      try { dispatch(hideLoader()); } catch(_) {}
      // Store challenge in Redux (ephemeral) and navigate
      try { dispatch({ type: SET_AUTH_CHALLENGE, payload: { type: 'MFA', user: { username: cognitoUser.username, sub: canonicalId } } }); } catch(_) {}
      try { history.replace('/mfa'); } catch(_) {}
      return { success: false, is_mfa: true, user: cognitoUser, message: 'mfa-required' };
      } else if (cognitoUser?.challengeName === 'NEW_PASSWORD_REQUIRED') {
      log('challengeIntercept', { challenge: 'NEW_PASSWORD_REQUIRED' });
      try { dispatch(hideLoader()); } catch(_) {}
      try { dispatch({ type: SET_AUTH_CHALLENGE, payload: { type: 'FORCE_PASSWORD', user: { username: cognitoUser.username, sub: canonicalId } } }); } catch(_) {}
      try { history.replace('/change-password'); } catch(_) {}
      return { success: false, is_force: true, user: cognitoUser, message: 'new-password-required' };
    }
    // Handle Google linking intent using universal backend:
    // When returning from Google sign-in, session is Google source user and we stored linkOriginalSub (destination password user) earlier.
    // Call link endpoint with destination_sub (mode 2) and finish without extra reauth cycle.
    try {
      const linkIntent = sessionStorage.getItem('linkIntent');
      if (linkIntent === 'google') {
        const destinationSub = sessionStorage.getItem('linkOriginalSub');
        if (destinationSub && destinationSub !== cognitoUser.username) {
          const accountId = store.getState().session?.account?.account_id;
          if (accountId) {
            try {
              const linkToken = store.getState().session?.token;
              const resp = await API.post(Gateway.name, `/users/link-google/${accountId}/${destinationSub}`, {
                headers: { Authorization: linkToken },
                body: { destination_sub: destinationSub }
              });
              if (resp) {
                if (resp.code === 'GOOGLE_ALREADY_LINKED') {
                  dispatch(showNotification('error', 'Google Already Linked', 'That Google account is already linked to another user.'));
                } else if (resp.code === 'ALREADY_LINKED') {
                  dispatch(showNotification('warning', 'Already Linked', 'Google sign-in was already enabled for this account.'));
                } else if (resp.code === 'LINKED') {
                  dispatch(showNotification('success', 'Google Linked', 'Google sign-in enabled.'));                     
                } else if (resp.success === false) {
                  dispatch(showNotification('error', 'Link Failed', resp.message || 'Link failed'));
                } else {
                  dispatch(showNotification('info', 'Google Link', 'No change.'));
                }
                // If we are in Google session mode (destination_sub path) revert to password user if we stored originalSub
                if ((resp.code === 'LINKED' || resp.code === 'ALREADY_LINKED') && destinationSub && destinationSub !== cognitoUser.username) {
                  try {
                    await Auth.signOut();
                    // We cannot auto sign-in without password; show notification so user signs back in.
                    dispatch(showNotification('info', 'Session Refresh', 'Sign back in (or use Google) to continue.'));
                  } catch(_) {}
                }
              }
            } catch(eLink) {
              dispatch(showNotification('error', 'Link Failed', eLink?.message || 'Could not link Google account'));
            }
          } else {
            dispatch(showNotification('error', 'Link Delayed', 'Account not ready; refresh and try again.'));
          }
        }
        sessionStorage.removeItem('linkIntent');
        sessionStorage.removeItem('linkOriginalSub');
      }
    } catch(_) {}
    const JWT = cognitoUser.signInUserSession.idToken.jwtToken;
    // Preserve existing credentials (partner, phone, etc.) while updating Cognito-specific fields
    const existingCredentials = store.getState().authentication?.cognito || {};
    dispatch({ type: UPDATE_CREDENTIALS, payload: { ...existingCredentials, username: cognitoUser.username, preferredMFA: cognitoUser.preferredMFA, attributes: cognitoUser.attributes } });
    dispatch({ type: SET_TOKEN, payload: JWT });

  // Use canonicalId for accounts fetch (second parameter also user identity)
  let accounts = await dispatch(getAccounts(canonicalId, canonicalId, true));
  log('postGetAccountsDispatch', { raw: !!accounts });
    let linkingReauth = false; try { linkingReauth = !!sessionStorage.getItem('linkReauth'); } catch(_) {}
    if (accounts && !Array.isArray(accounts?.accounts) && Array.isArray(accounts?.payload)) {
      accounts = { ...accounts, accounts: accounts.payload };
    }
    const accountsFailed = accounts && accounts.success === false;
    log('accounts fetch result', { present: !!accounts, length: accounts?.accounts?.length, failed: accountsFailed });
    log('accountsFetched', { failed: accountsFailed, len: accounts?.accounts?.length || 0, hasList: Array.isArray(accounts?.accounts) });

  // If no accounts found with canonicalId (stable sub), try legacy Google_ username for backward compatibility
  const noAccountsFound = accountsFailed || !accounts || !Array.isArray(accounts.accounts) || !accounts.accounts.length;
  const looksGoogleFederated = /^Google_/.test(cognitoUser?.username || '');
  if (noAccountsFound && looksGoogleFederated && cognitoUser?.username !== canonicalId) {
    log('trying legacy Google_ username lookup', { username: cognitoUser.username, canonicalId });
    const legacyAccounts = await dispatch(getAccounts(cognitoUser.username, cognitoUser.username, true));
    if (legacyAccounts && Array.isArray(legacyAccounts.accounts) && legacyAccounts.accounts.length > 0) {
      log('found accounts with legacy Google_ username, migrating to stable sub');
      accounts = legacyAccounts;
    }
  }

  if (accountsFailed || !accounts || !Array.isArray(accounts.accounts) || !accounts.accounts.length) {
      if (linkingReauth) {
        try { await Auth.signOut(); } catch(_) {}
        try { sessionStorage.removeItem('linkReauth'); } catch(_) {}
        dispatch(showNotification('info', 'Session Refresh', 'Please sign in again to finalize Google linking.'));
        log('linkingReauthPath');
        return { success: false, message: 'linking-reauth' };
      }
      // Suppression only if an actual link flag still active (not pure Google-first login)
      let hasLinkFlag = false; try { hasLinkFlag = !!sessionStorage.getItem('linkAutoReauth') || !!sessionStorage.getItem('linkIntent'); } catch(_) {}
      const looksGoogleEphemeral = /^Google_/.test(cognitoUser?.username || '');
      if (hasLinkFlag && looksGoogleEphemeral) {
        log('suppressing provisioning for ephemeral Google identity post-link');
        try { await Auth.signOut(); } catch(_) {}
        dispatch(showNotification('info', 'Finalizing Link', 'Refreshing your session.'));
        log('suppressedEphemeral', { hasLinkFlag, looksGoogleEphemeral });
        return { success: false, message: 'suppressed-ephemeral-provisioning' };
      }
      const attributes = cognitoUser.attributes || {};
      const token = cognitoUser.signInUserSession?.idToken?.payload || {};
      let email = attributes.email || attributes.preferred_username || token.email || token.preferred_username || null;
      if (!email) {
        dispatch(showNotification('error', 'Sign In Incomplete', 'Could not retrieve an email address from identity provider.'));
        log('noEmailAbort');
        return { success: false };
      }
      // Check if this email already exists in the database
      let accountFoundByEmail = false;
      try {
        const ce = await checkEmail(email, { detail: true });
        if (ce && ce.available === false) {
          // If email exists with a Google federated account, try to fetch it
          if (ce.is_federated && ce.cognito_id) {
            log('found existing Google account by email, attempting to fetch', { cognito_id: ce.cognito_id });
            const legacyAccounts = await dispatch(getAccounts(ce.cognito_id, ce.cognito_id, true));
            if (legacyAccounts && Array.isArray(legacyAccounts.accounts) && legacyAccounts.accounts.length > 0) {
              log('successfully loaded existing Google account', { cognito_id: ce.cognito_id });
              accounts = legacyAccounts;
              accountFoundByEmail = true;
              // Skip provisioning - we found the existing account
            } else {
              // Federated account found in DB but couldn't fetch - this is an error state
              try { await Auth.signOut(); } catch(_) {}
              dispatch(showNotification('error', 'Sign In Error', 'Could not load your account. Please contact support.'));
              log('federatedAccountFetchFailed', { cognito_id: ce.cognito_id });
              return { success: false };
            }
          } else {
            // Email exists with a password-based account
            try { await Auth.signOut(); } catch(_) {}
            dispatch(showNotification('error', 'Account Exists', 'This email is already registered with a password. Please sign in using Email & Password.'));
            try { history.replace('/login?forceGoogle=1'); } catch(_) {}
            log('emailConflictAbort');
            return { success: false };
          }
        }
      } catch(_) {}
      
      // Only provision if we didn't find an existing account by email
      let provision;
      // Check Redux for provisioning metadata from confirmSignup flow (must be outside if block for cleanup)
      const cognito = store.getState().authentication?.cognito || {};
      const hasPendingMeta = cognito.pendingProvisioning === true;
      
      if (!accountFoundByEmail) {
        const fullNameSource = attributes.name || token.name || '';
        const parts = fullNameSource.split(/\s+/).filter(Boolean);
        let first_name = attributes.given_name || token.given_name || parts[0] || 'User';
        let last_name = attributes.family_name || token.family_name || (parts.length > 1 ? parts.slice(1).join(' ') : '');
        if (!last_name && first_name === 'User') last_name = 'User';
        const avatar = token.picture || attributes.picture || null;
        provision = await provisionInitialSession({
          cognitoUser,
          email,
          first_name: hasPendingMeta && cognito.first_name ? cognito.first_name : first_name,
          last_name: hasPendingMeta && cognito.last_name ? cognito.last_name : last_name,
          phone: hasPendingMeta && cognito.phone ? cognito.phone : null,
          partner: hasPendingMeta && cognito.partner === true ? true : false,
          referral: hasPendingMeta && cognito.referral ? cognito.referral : null,
          company_name: hasPendingMeta && cognito.company_name ? cognito.company_name : null,
          invite: hasPendingMeta && cognito.invite ? cognito.invite : null,
          npi_number: hasPendingMeta && cognito.npi_number ? cognito.npi_number : null,
          npi_data: hasPendingMeta && cognito.npi_data ? cognito.npi_data : null,
          npi_looked_up_at: hasPendingMeta && cognito.npi_looked_up_at ? cognito.npi_looked_up_at : null,
          debugSource: hasPendingMeta ? 'confirmSignup-redux' : 'federated',
          avatar
        });
      }
      // Clear pending flag after provisioning
      if (hasPendingMeta) {
        try {
          dispatch({
            type: UPDATE_CREDENTIALS,
            payload: { pendingProvisioning: false }
          });
        } catch(_) {}
      }
      log('provisionResult', { success: !!provision?.success });
      if (!provision?.success) {
        log('provisionFailed');
        return provision; // may contain notified flag
      }
      // provision already dispatched IS_LOGGED_IN
      if (!store.getState().session.authorized) { try { dispatch({ type: IS_LOGGED_IN }); log('fallbackAuthorizeAfterProvision'); } catch(_) {} }
      // Clear any stale lock state after successful provisioning
      try { dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } }); } catch(_) {}
      log('provisionSuccessEnd');
      return provision;
    }

    try { sessionStorage.removeItem('federatedAuthFailShown'); } catch(_) {}
  // Single authoritative IS_LOGGED_IN dispatch here (ensure authorized flag even if downstream fetches fail)
  dispatch({ type: IS_LOGGED_IN });
  log('authorizedDispatchExistingAccounts', { authorized: store.getState().session.authorized });
    try { dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } }); } catch(_) {}
    let userData = null;
    // Fallback: derive account id from accounts list if current missing (rare race)
    const fallbackAccountId = accounts?.current?.account_id || (Array.isArray(accounts?.accounts) && accounts.accounts[0]?.account_id) || null;
    try {
      if (fallbackAccountId) {
        userData = await getUser(canonicalId, fallbackAccountId, true);
      } else {
        log('userFetchSkippedNoAccount');
      }
    } catch(userErr) { log('userFetchError', { message: userErr?.message }); }
    log('userFetchComplete', { userSuccess: !!userData?.success, hadFallback: !!fallbackAccountId });

    if (userData?.success) {
      const u = userData.payload;
      try { LogRocket.identify(u.cognito_id, { name: `${u.first_name} ${u.last_name}`.trim(), email: u.email, account_id: accounts.current.account_id, environment: process.env.REACT_APP_STAGE || 'development' }); } catch(_) {}
      try { setCrisp({ ...u, partner: accounts.current?.partner, company_name: accounts.current?.company_name }); } catch(_) {}
    }

    if (fallbackAccountId && store.getState().authentication?.user?.cognito_id) {
      try {
        await dispatch(getProjects(true));
        log('projectsFetchComplete');
      } catch(projErr) { log('projects fetch exception', projErr); log('projectsFetchError', { message: projErr?.message }); }
    } else {
      log('projectsFetchSkipped', { hasUser: !!store.getState().authentication?.user?.cognito_id, fallbackAccountId });
    }
  if (!store.getState().session.authorized) { try { dispatch({ type: IS_LOGGED_IN }); log('fallbackAuthorizeEnd', { secondSet: true }); } catch(_) {} }
    // Final safety: clear lock before returning success
    try { dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } }); } catch(_) {}
    log('end', { success: true, authorized: store.getState().session.authorized });
    // Safety: if still not authorized, force set (should be extremely rare)
    if (!store.getState().session.authorized) {
      dispatch({ type: IS_LOGGED_IN });
      log('forceAuthorizeAfterEnd');
    }
    const bootstrapPayload = { success: true, accounts };
    if (detailedReturn) return bootstrapPayload;
    return bootstrapPayload;
  } finally {
    try { dispatch(hideLoader()); log('hideLoaderFinally'); } catch(_) {}
  }
};

// Concurrency guards to prevent duplicate provisioning when both signIn and cognitoHostedUI fire
let bootstrapInFlight = false;
let bootstrapCompleted = false;

// Centralized Hub listener to trigger bootstrap on successful sign in / hosted UI flows
Hub.listen('auth', async ({ payload }) => {
  const event = payload.event;
  if (__suppressNextBootstrap) {
    __suppressNextBootstrap = false;
    return; // skip duplicate bootstrap
  }
  const clearFederatedFlag = () => { try { sessionStorage.removeItem('federatedInFlight'); } catch(_) {} };
  if (event === 'signIn' || event === 'cognitoHostedUI') {
    if (bootstrapCompleted || bootstrapInFlight) return;
    bootstrapInFlight = true;
    try {
      const result = await store.dispatch(bootstrapSession({ provisioningHint: 'Authorizing...' }));
      if (result?.success) {
        bootstrapCompleted = true;
          // One-time post-bootstrap passkey enforcement for federated flows (manual signIn handles its own)
          try {
            if (!__passkeyBootstrapDone) {
              const attempt = (tries = 0) => {
                const st = store.getState();
                const passkeys = st.authentication?.user?.passkeys || [];
                const hasValid = Array.isArray(passkeys) && passkeys.some(pk => pk?.origin === window.location.origin);
                if (hasValid) {
                  __passkeyBootstrapDone = true;
                  setTimeout(async () => {
                    const authed = await passkeyAuth();
                    if (!authed) store.dispatch({ type: SET_LOCKED, payload: { show: true, inactive: false } });
                  }, 60);
                } else if (tries < 10) { // retry up to ~1s
                  setTimeout(() => attempt(tries + 1), 100);
                }
              };
              attempt();
            }
          } catch(_) {}
      } else if (result?.is_mfa || result?.is_force) {
        // Challenge dispatched earlier; mark phase and allow UI to render challenge route.
        bootstrapInFlight = false;
      } else {
        bootstrapInFlight = false;
        const benignCodes = ['linking-reauth', 'suppressed-ephemeral-provisioning', 'linking-initiated-reauth'];
        const reason = result?.message || result?.stage || 'unknown';
        const previouslyNotified = result?.notified;
        if (!previouslyNotified && !benignCodes.includes(reason)) {
          try { store.dispatch(showNotification('error', 'Sign In Incomplete', 'We could not finish setting up your session. Please try again.')); } catch(_) {}
        }
      }
    } catch(err) {
      bootstrapInFlight = false;
      try {
        if (!sessionStorage.getItem('federatedAuthFailShown')) {
          store.dispatch(showNotification('error', 'Sign In Failed', 'Could not initialize session. Reloading...'));
          sessionStorage.setItem('federatedAuthFailShown', '1');
        }
      } catch(_) {}
      setTimeout(() => window.location.reload(), 1500);
    } finally { clearFederatedFlag(); try { store.dispatch(hideLoader()); } catch(_) {} }
  } else if (event === 'signIn_failure' || event === 'cognitoHostedUI_failure') {
    try { store.dispatch(hideLoader()); } catch(_) {}
    clearFederatedFlag();
    try {
      const reason = payload?.data?.message || payload?.data?.error || '';
      const isUserCancel = /cancelled|cancel|closed|dismissed/i.test(reason);
      if (!isUserCancel) {
        store.dispatch(showNotification('error', 'Google Sign-In Failed', 'Could not complete sign-in with Google. Please try again.'));
      }
    } catch(_) {}
  }
});

export const setCrisp = (u) => {
  try {
    // Basic guards
    if (!u || !u.cognito_id) return;
    // Prevent duplicate init for same user
    if (window.__CRISP_LAST_ID === u.cognito_id && window.__CRISP_INIT_DONE) return;

    // Skip if email not yet available; schedule a one-time retry (common early federated race)
    if (!u.email) {
      if (!window.__CRISP_EMAIL_RETRY_SCHEDULED) {
        window.__CRISP_EMAIL_RETRY_SCHEDULED = true;
        setTimeout(() => {
          try {
            const st = store.getState();
            const usr = st.authentication?.user;
            if (usr?.email) setCrisp({ ...usr, partner: usr.partner, company_name: usr.company_name });
          } catch(_) {}
        }, 250);
      }
      return;
    }

    // Build session data object
    const sessionData = {
      cognito_id: u.cognito_id,
      partner: u.partner || false
    };
    if (u.gender) sessionData.gender = u.gender;

    // Configuration
    Crisp.muteSound(false);
    Crisp.setAvailabilityTooltip(false);
    Crisp.setHideOnMobile(true);

    // Set token ID for session continuity
    Crisp.setTokenId(u.cognito_id);

    // Enable safe mode in production (after other config)
    if (process.env.REACT_APP_STAGE === 'production') {
      Crisp.setSafeMode(true);
    }

    // Set session segments (environment)
    Crisp.session.setSegments([process.env.REACT_APP_STAGE || 'development']);

    // Set session data
    Crisp.session.setData(sessionData);

    // Set user info
    Crisp.user.setEmail(u.email);
    Crisp.user.setNickname(`${u.first_name || ''} ${u.last_name || ''}`.trim());
    if (u.phone) {
      Crisp.user.setPhone(u.phone);
    }
    if (u.company_name) {
      Crisp.user.setCompany(u.company_name);
    }
    Crisp.user.setAvatar(buildAvatarUrl(u.cognito_id, u.updated_at, u.avatar));

    // Attach event listeners only once
    if (!window.__CRISP_LISTENERS_ATTACHED) {
      window.__CRISP_LISTENERS_ATTACHED = true;

      // Listen for message received event
      Crisp.message.onMessageReceived(() => {
        try {
          const unread = Crisp.chat.unreadCount();
          store.dispatch(updateChatBadge(unread));
        } catch(_) {}
      });

      // Listen for chat closed event
      Crisp.chat.onChatClosed(() => {
        try {
          const unread = Crisp.chat.unreadCount();
          store.dispatch(updateChatBadge(unread));
          Crisp.chat.close();
          Crisp.chat.hide();
          Crisp.message.markAsRead();
        } catch(_) {}
      });
    }

    // Add LogRocket URL to session data (when available)
    LogRocket.getSessionURL((url) => {
      if (url) {
        Crisp.session.setData({ logrocket_url: url });
      }
    });

    // Hide chat by default
    Crisp.chat.hide();
    
    // Load Crisp
    Crisp.load();

    // Mark as initialized
    window.__CRISP_INIT_DONE = true;
    window.__CRISP_LAST_ID = u.cognito_id;
  } catch(_) {}
};

export const verifyCredential = async (credential, challenge) => {
  return API.post(
    Gateway.name,
    `/users/verify-credential/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`,
    {
      headers: {
        Authorization: store.getState().session.token
      },
      body: {
        credential,
        challenge
      }
    })
    .then((response) => response)
    .catch((error) => {
      return error;
    });
};

export const storeCredential = async (credential, challenge, origin) => {
  return API.post(
    Gateway.name,
    `/users/store-credential/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`,
    {
      headers: {
        Authorization: store.getState().session.token
      },
      body: {
        credential,
        challenge
      }
    })
    .then((response) => response)
    .catch((error) => {
      return { success: false, message: error.response && error.response.data ? error.response.data.message : "Something went wrong." };
    });
};

export const getAuthChallenge = async (verify) => {
  return API.get(
    Gateway.name,
    `/users/get-auth-challenge/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}?verify=${verify}`,
    {
      headers: {
        Authorization: store.getState().session.token
      }
    })
    .then((response) => response)
    .catch((error) => {
      return { success: false, message: error.response && error.response.data ? error.response.data.message : "Something went wrong." };
    });
};

export const deletePasskey = async (id) => {
  return API.del(
    Gateway.name,
    `/users/delete-passkey/${id}/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`,
    {
      headers: {
        Authorization: store.getState().session.token
      }
    })
    .then(async (response) => {
      await getUser(store.getState().authentication.user.cognito_id, store.getState().session.account.account_id, true);
      return response;
    })
    .catch((error) => {
      debug.error('Store credential error', error);
      return { success: false, message: error.response && error.response.data ? error.response.data.message : "Something went wrong." };
    });
};

export const editPasskeyName = async (id, name) => {
  return API.patch(
    Gateway.name,
    `/users/edit-passkey/${id}/${(store.getState().session.account.account_id)}/${store.getState().authentication.user.cognito_id}`,
    {
      headers: {
        Authorization: store.getState().session.token
      },
      body: {
        name
      }
    })
    .then(async (response) => {
      await getUser(store.getState().authentication.user.cognito_id, store.getState().session.account.account_id, true);
      return response;
    })
    .catch((error) => {
      debug.error('Verify credential error', error);
      return { success: false, message: error.response && error.response.data ? error.response.data.message : "Something went wrong." };
    });
};

export const signUp = ({
  email,
  password,
  phone,
  first_name,
  last_name,
  invite,
  partner = false,
  company_name,
  npi_number = null,
  npi_data = null,
  npi_looked_up_at = null
}) => async (dispatch) => {
  const user_email = email.toLowerCase().replace(/\s+/g, "");
  const attributes = {
    email: user_email,
    name: `${first_name} ${last_name}`
  };
  if (phone) attributes.phone_number = formatUSPhoneNumber(phone);

  dispatch(showLoader("Loading..."));

  try {
    await checkEmail(user_email);
    return Auth.signUp({
      username: attributes.email,
      password,
      attributes
    })
    .then((cognitoUser) => {
      dispatch({
        type: UPDATE_CREDENTIALS,
        payload: {
          company_name,
          partner,
          invite,
          verifying: true,
          first_name,
          last_name,
          email: user_email,
          phone,
          password,
          username: cognitoUser.userSub,
          codeDeliveryDetails: cognitoUser.codeDeliveryDetails,
          npi_number,
          npi_data,
          npi_looked_up_at
        }
      });
      LogRocket.identify(cognitoUser.userSub, {
        name: `${first_name} ${last_name}`,
        email: user_email,
        environment: process.env.REACT_APP_STAGE || "development"
      });
      dispatch(hideLoader());
      return { success: true, payload: { ...cognitoUser, email: user_email, first_name, last_name } };
    })
    .catch(async (e) => {
      dispatch(hideLoader());
      const isUsernameExists = e.code === "UsernameExistsException" || e.name === "UsernameExistsException";
      if (isUsernameExists) {
        try {
          const data = await API.post(Gateway.name, "/users/cancel-signup", { body: { email: user_email } });
          const recovered = !!data?.deleted;
          return {
            success: false,
            recovered,
            message: recovered
              ? "You started signing up before but didn't finish. We've cleared that—please try again."
              : (data?.message || "This email is already registered. Please sign in.")
          };
        } catch (err) {
          const msg = err.response?.data?.message || "This email is already registered. Please sign in.";
          return { success: false, recovered: false, message: msg };
        }
      }
      return {
        success: false,
        error: e,
        message: e.message || "Registration failed."
      };
    });
  } catch (e) {
    dispatch(hideLoader());
    if (e.response && e.response.data && e.response && e.response.data.status_code === "blacklist_domain") store.dispatch({ type: CLEAR_ALL });
    return { success: false, message: e.response && e.response.data ? e.response.data.message : e.message || "Something went wrong." };
  }
};

export const confirmSignup = (email, code, cognito, referral) => async (dispatch) => {
  dispatch(showLoader("Loading..."));
  try {
    await Auth.confirmSignUp(email, code, "SMS_MFA");
    __suppressNextBootstrap = true;
    const cognitoUser = await Auth.signIn(email, cognito.password);
    debug.log('authentication', 'Confirmed signup and signed in user', { username: cognitoUser?.username });
    // Store provisioning metadata in Redux (already stored during signUp)
    dispatch({
      type: UPDATE_CREDENTIALS,
      payload: {
        partner: cognito.partner,
        company_name: cognito.company_name,
        invite: cognito.invite,
        referral,
        phone: cognito.phone,
        first_name: cognito.first_name,
        last_name: cognito.last_name,
        npi_number: cognito.npi_number,
        npi_data: cognito.npi_data,
        npi_looked_up_at: cognito.npi_looked_up_at,
        pendingProvisioning: true
      }
    });
    const result = await dispatch(bootstrapSession({ provisioningHint: 'Setting up your account...' }));
    dispatch(hideLoader());
    return result;
  } catch (e) {
  debug.error('Authentication error', e);
    dispatch({ type: IS_LOGGED_OUT });
    dispatch(hideLoader());
    return { success: false };
  }
};

export const resendSignup = (email) => async (dispatch) => {
  email = email.toLowerCase().replace(/\s+/g, "");

  try {
    await Auth.resendSignUp(email);
    dispatch(showNotification("success", "A verification code has been sent to you."));
    return { success: true };
  } catch (error) {
    dispatch(showNotification("error", "Something went wrong.", "Could not resend verification code."));
    return {
      success: false,
      message: error.response && error.response.data ? error.response.data.message : "Something went wrong."
    };
  }
};

export const signIn = async ({ email, password } = {}, cognito) => {
  const dispatch = store.dispatch;
  const rawEmail = email ?? cognito?.attributes?.email ?? cognito?.username ?? '';
  const user_email = typeof rawEmail === 'string' ? rawEmail.toLowerCase().replace(/\s+/g, '') : '';
  try {
    if ((user_email && password) || cognito) {
      let cognitoUser;
      if (cognito) cognitoUser = cognito;
      else cognitoUser = await Auth.signIn(user_email, password);
      if (cognitoUser?.challengeName === "SMS_MFA" || cognitoUser?.challengeName === "SOFTWARE_TOKEN_MFA") {
        dispatch(hideLoader());
        try {
          // Store the Cognito user by reference so NewMFA can call Auth.confirmSignIn(user, code, challengeName); add email for display.
          if (!cognitoUser.email) cognitoUser.email = cognitoUser.attributes?.email || cognitoUser.username;
          dispatch({ type: UPDATE_CREDENTIALS, payload: cognitoUser });
          dispatch({ type: SET_AUTH_CHALLENGE, payload: { type: 'MFA', user: { username: cognitoUser.username, sub: cognitoUser.attributes?.sub || cognitoUser.username } } });
        } catch (_) {}
        return { success: false, is_mfa: true, user: cognitoUser };
      } else if (cognitoUser?.challengeName === "NEW_PASSWORD_REQUIRED") {
        dispatch(hideLoader());
        try {
          if (!cognitoUser.email) cognitoUser.email = cognitoUser.attributes?.email || cognitoUser.username;
          dispatch({ type: UPDATE_CREDENTIALS, payload: cognitoUser });
          dispatch({ type: SET_AUTH_CHALLENGE, payload: { type: 'FORCE_PASSWORD', user: { username: cognitoUser.username, sub: cognitoUser.attributes?.sub || cognitoUser.username } } });
        } catch (_) {}
        return { success: false, is_force: true, user: cognitoUser };
      } else {
        // Suppress duplicate bootstrap from Hub 'signIn'
        __suppressNextBootstrap = true;
        const session = await dispatch(bootstrapSession({ provisioningHint: 'Loading...', detailedReturn: true }));
        if (!session?.success) return { success: false };
        if (!store.getState().session.authorized) {
          try { dispatch({ type: IS_LOGGED_IN }); } catch(_) {}
        }
        try { dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } }); } catch(_) {}
        let passkeyLocked = false;
        const state = store.getState();
        const passkeys = state.authentication?.user?.passkeys || [];
        const hasValidPasskeys = Array.isArray(passkeys) && passkeys.some(pk => pk?.origin === window.location.origin);
        if (hasValidPasskeys) {
          await new Promise(r => setTimeout(r, 120));
          const authed = await passkeyAuth();
            if (!authed) {
              passkeyLocked = true;
              dispatch({ type: SET_LOCKED, payload: { show: true, inactive: false } });
            } else {
              dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } });
            }
        } else {
          try { dispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } }); } catch(_) {}
        }
        if (passkeyLocked) return { success: false };
        return { success: true, accounts: session.accounts, projects: session.projects || [], account: session.accounts?.current };
      }
    } else if (store.getState().session.authorized) {
      return dispatch(tokenRefresh(store.getState().authentication.user?.last_update));
    } else {
      dispatch(hideLoader());
      return { success: false };
    }
  } catch (error) {
    if (error.code === "UserNotConfirmedException") {
      return { success: false, is_unconfirmed: true, challengeName: error.code, user: { email: user_email } }
    } else if (error.code === "PasswordResetRequiredException") {
      dispatch({ type: UPDATE_CREDENTIALS, payload: { challengeName: error.code, email: user_email } });
      externalNavigate("/change-password");
    } else if (error.code === "NotAuthorizedException" || error.code === "UserNotFoundException") {
      return { success: false, error, message: "NotAuthorizedException" };
    } else {
      return { success: false, error, message: "UserNotFoundException" };
    }
    externalNavigate("/");
    checkOnlineStatus();
    dispatch(hideLoader());
    return false;
  }
};

export const cancelSignup = async (email) => {
  if (email) {
    email = email.toLowerCase().replace(/\s+/g, "");
    return API.post(Gateway.name, `/users/cancel-signup`, { body: { email } })
      .then((data) => {
        return data.deleted;
      })
      .catch((error) => {
        return {
          success: false,
          message: error.response && error.response.data ? error.response.data.message : "Something went wrong."
        };
      });
  }
  return {
    success: false,
    message: "Could not cancel registration. Please provide a valid email address."
  };
};

// Idempotent sign out action; avoids race where first click triggers partial cleanup and second completes.
let __signOutInFlight = false;
export const signOut = (global = false) => async (dispatch) => {
  if (__signOutInFlight) {
    return false; // ignore rapid subsequent invocations
  }
  __signOutInFlight = true;
  try {
    dispatch({ type: LOGGING_OUT });
    // Immediately mark logged out so routing stops trying to compute post-auth redirects
    try { dispatch({ type: IS_LOGGED_OUT }); } catch(_) {}
    try {
      await Auth.signOut({ global });
    } catch (primaryErr) {
      // Amplify may throw if already signed out / no current user; treat as success path for UI consistency
    debug.warn('Sign out error treated as non-fatal', primaryErr);
    }
    try { themeBuilder().reset(); } catch(_) {}
    try {
      if (window.$crisp?.do) {
        Crisp.chat.hide();
        Crisp.setTokenId();
        Crisp.session.reset();
        window.CRISP_TOKEN_ID = null;
      }
    } catch(_) {}
    try { localStorage.removeItem('ably_token'); } catch(_) {}
    try { store.dispatch({ type: SET_ABLY_TOKEN, payload: null }); } catch(_) {}
    try { store.dispatch({ type: SET_SESSION_START_TIME, payload: null }); } catch(_) {}
    try { clearObjectStore(config.system.IDB_NAME, config.system.IDB_STORE); } catch(_) {}
    try { store.dispatch({ type: CLEAR_ALL }); } catch(_) {}
    __localSignOutCompleted = true;
    // Hard replace to /login
    try { if (window.location.pathname !== '/login') window.location.replace('/login'); } catch(_) {}
    return true;
  } catch (e) {
  debug.error('[SIGN_OUT] fatal', e);
    return false;
  } finally {
    setTimeout(() => { __signOutInFlight = false; }, 750); // small debounce window
  }
};

const promptLogin = (message, okText) => {
  const promptOptions = {
    onOk: () => store.dispatch(signOut()),
    onCancel: () => toastr.removeByType("confirms"),
    okText,
    cancelText: "Cancel",
    disableCancel: true,
  };
  toastr.confirm(message, promptOptions);
};

// To initiate the process of verifying the attribute like 'phone_number' or 'email'
export const verifyCurrentUserAttribute = async (attr) => {
  try {
    // Simply request verification code from Cognito
    // The backend has already updated the phone_number attribute when the user saved their profile
    // We should NOT call Auth.updateUserAttributes here as it creates duplicate verification attempts
    await Auth.verifyCurrentUserAttribute(attr);
    return { success: true, message: 'Verification code sent successfully.' };
  } catch (err) {
    // Cognito phone/email attribute verification requires access token scope 'aws.cognito.signin.user.admin'.
    // If missing, API returns NotAuthorizedException mentioning required scopes.
    if (err?.__type === 'NotAuthorizedException' && /required scopes/i.test(err?.message || '')) {
      try {
        const cognitoUser = await Auth.currentAuthenticatedUser();
        const access = cognitoUser?.signInUserSession?.accessToken?.jwtToken;
        if (access) {
          const payload = JSON.parse(atob(access.split('.')[1] || ''));
          const scopes = payload?.scope || payload?.scopes || '';
          debug.log('authentication', '[VERIFY][SCOPE_DIAGNOSTICS]', { scopes });
        }
      } catch(_) {}
      return { success: false, error: err, message: 'Session missing aws.cognito.signin.user.admin scope. Please sign out and sign back in to refresh tokens, then retry phone verification.' };
    }
    return { success: false, error: err, message: err.message };
  }
};

// To verify attribute with the code
export const verifyCurrentUserAttributeSubmit = async (attr, verificationCode) => {
  return Auth.verifyCurrentUserAttributeSubmit(attr, verificationCode)
    .then(async (v) => {
      await getUser(store.getState().authentication.user.cognito_id, store.getState().session.account.account_id, true);
      return { success: true, message: "Verified successfully." };
    })
    .catch((err) => {
      return { success: false, error: err, message: err.message };
    });
};

export const completeNewPassword = async (cognito, email, password) => {
  email = email.toLowerCase().replace(/\s+/g, "");
  try {
    return Auth.completeNewPassword(
      cognito,
      password
    )
    .then(async (loggedInUser) => {
      await signIn({ email, password });
      return loggedInUser;
    })
    .catch((error) => {
      store.dispatch(showNotification("error", "Could not update password.", error.message));
    });
  } catch (err) {
  debug.error('Password update error', err);
    store.dispatch(showNotification("error", "Could not update password.", err.message));
    return err;
  }
};

export const changePassword = async (oldPassword, newPassword) => {
  try {
    const user = await Auth.currentAuthenticatedUser();
    await Auth.changePassword(user, oldPassword, newPassword);
    store.dispatch(showNotification("success", "Password Updated", "Your password was successfully changed."));
    return true;
  } catch (err) {
    store.dispatch(showNotification("error", "Password Update Failed", err.message));
    return false;
  }
};

export const forgotPassword = (email) => async (dispatch) => {
  try {
    await Auth.forgotPassword(email);
    dispatch(showNotification("success", "Password verification code has been sent to your email."));
    return { success: true };
  } catch (error) {
    debug.error('Forgot password error', error);
    dispatch(showNotification("error", "Could not send password reset.", error.message));
    return { success: false, error };
  }
};

export const forgotPasswordSubmit = (email, code, newPassword) => async (dispatch) => {
  try {
    await Auth.forgotPasswordSubmit(email, code, newPassword);
    dispatch(showNotification("success", "Password successfully updated. Logging in..."));
    dispatch(showLoader("Loading..."));
    return { success: true };
  } catch (error) {
    debug.error('Password reset validation error', error);
    dispatch(showNotification("error", "Validation Failed - Could not update password.", error.message));
    return { success: false, error };
  }
};

export const setupMfa = async (type) => {
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser();
    const log = (...a) => debug.log('cognito', '[MFA][setupMfa]', ...a);
    log('requested type', type);
    // If enabling SMS_MFA ensure phone attribute is verified; if not, surface error fast.
    if (type === 'SMS_MFA') {
      const verified = (cognitoUser?.attributes?.phone_number_verified === true || cognitoUser?.attributes?.phone_number_verified === 'true');
      if (!verified) {
        log('aborting enable; phone not verified');
        return { success: false, error: { message: 'Phone number not verified' } };
      }
    }
    await Auth.setPreferredMFA(cognitoUser, type);
    // Force a fresh get to avoid cached preferredMFA
    const preferred = await Auth.getPreferredMFA(cognitoUser, { bypassCache: true }).catch(e => { log('getPreferredMFA error', e?.message); return null; });
    log('post-set preferred', preferred);
    if (preferred) {
      // Refresh attributes snapshot so downstream signIn comparisons see latest
      let refreshedUser = cognitoUser;
      try { refreshedUser = await Auth.currentAuthenticatedUser({ bypassCache: true }); } catch(_) {}
      store.dispatch({
        type: UPDATE_CREDENTIALS,
        payload: {
          username: refreshedUser.username,
          preferredMFA: preferred,
          attributes: refreshedUser.attributes
        }
      });
      return { success: true, data: preferred };
    }
    return { success: false };
  } catch (error) {
    debug.error('Error setting MFA', error);
    return { success: false, error };
  }
};

export const rememberMFADevice = async () => {
  try {
    const result = await Auth.rememberDevice();
    return result;
  } catch (error) {
    debug.error('Error remembering device', error);
    return error;
  }
};

export const forgetMFADevice = async () => {
  try {
    const result = await Auth.forgetDevice();
    return result;
  } catch (error) {
    debug.error('Error forgetting device', error);
    return error;
  }
};

export const fetchMFADevices = async () => {
  // Throttle & cache to avoid hammering Cognito (some components re-render rapidly)
  const FIVE_MIN = 5 * 60 * 1000;
  if (!window.__mfaDevicesCache) {
    window.__mfaDevicesCache = { ts: 0, data: null, inFlight: null };
  }
  const cache = window.__mfaDevicesCache;
  const now = Date.now();
  if (cache.data && (now - cache.ts) < FIVE_MIN) return cache.data;
  if (cache.inFlight) return cache.inFlight;
  cache.inFlight = Auth.fetchDevices()
    .then((devices) => {
      cache.ts = Date.now();
      cache.data = devices || [];
      return cache.data;
    })
    .catch((error) => {
  debug.error('Error fetching device', error);
      return [];
    })
    .finally(() => {
      cache.inFlight = null;
    });
  return cache.inFlight;
};

export const autoVerifyingPhone = (payload) => async (dispatch) => {
  dispatch({ type: AUTO_VERIFYING_PHONE, payload });
};

let _lastTokenRefreshAttempt = 0;
let _tokenRefreshInFlight = false;
export const tokenRefresh = (last_update) => async (dispatch) => {
  const now = Date.now();
  if (_tokenRefreshInFlight) return true;
  if (now - _lastTokenRefreshAttempt < 5000) return true; // throttle multiple simultaneous calls

  const lastUpdateTimestamp = (last_update || 0) * 1000;
  const expirationTimestamp = lastUpdateTimestamp + 60 * 60 * 1000; // 1 hour
  const isExpired = !last_update || now > expirationTimestamp;
  if (!isExpired) {
    checkOnlineStatus();
    return true;
  }

  _lastTokenRefreshAttempt = now;
  _tokenRefreshInFlight = true;
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser();
    const currentSession = cognitoUser.signInUserSession;
    await new Promise((resolve, reject) => {
      cognitoUser.refreshSession(currentSession.refreshToken, (err, session) => {
        if (err || !session) return reject(err || new Error("No session returned"));
        try {
          const JWT = session.idToken.jwtToken;
          dispatch({ type: SET_TOKEN, payload: JWT });
          dispatch({
            type: UPDATE_CREDENTIALS,
            payload: {
              username: cognitoUser.username,
              preferredMFA: cognitoUser.preferredMFA,
              attributes: cognitoUser.attributes,
            },
          });
          const refreshCanonical = cognitoUser?.attributes?.sub || cognitoUser.username;
          getUser(refreshCanonical, store.getState().session.account.account_id, true)
            .then(async (user) => {
              if (user.success) {
                dispatch({ type: IS_LOGGED_IN });
                LogRocket.identify(user.payload.cognito_id, {
                  name: `${user.payload.first_name} ${user.payload.last_name}`,
                  email: user.payload.email,
                  account_id: store.getState().session.account?.account_id,
                  environment: process.env.REACT_APP_STAGE || "development",
                });
              }
              checkOnlineStatus();
              resolve(true);
              return user;
            })
            .catch((error) => {
              debug.error('Bootstrap user fetch error', error);
              checkOnlineStatus();
              resolve(false);
            });
        } catch (inner) {
          reject(inner);
        }
      });
    });
    return true;
  } catch (e) {
    checkOnlineStatus();
    if (store.getState().session.authorized) promptLogin("Your session has expired", "Log Out");
    return false;
  } finally {
    _tokenRefreshInFlight = false;
  }
};

const authListener = (data) => {
  switch (data.payload.event) {
    case "configured":
      logger.info("the Auth module is configured");
      break;
    case "signIn":
      logger.info("user signed in");
      break;
    case "signIn_failure":
      store.dispatch(hideLoader());
      store.dispatch(showNotification("error", `Sign In Failed - ${data.payload.data.message}`));
      logger.error("user sign in failed");
      break;
    case "signUp":
      logger.info("user signed up");
      break;
    case "signUp_failure":
      logger.error("user sign up failed");
      break;
    case "confirmSignUp":
      logger.info("user confirmation successful");
      break;
    case "completeNewPassword_failure":
      logger.error("user did not complete new password flow");
      break;
    case "autoSignIn":
      logger.info("auto sign in successful");
      break;
    case "autoSignIn_failure":
      logger.error("auto sign in failed");
      break;
    case "forgotPassword":
      logger.info("password recovery initiated");
      break;
    case "forgotPassword_failure":
      logger.error("password recovery failed");
      break;
    case "forgotPasswordSubmit":
      logger.info("password confirmation successful");
      break;
    case "forgotPasswordSubmit_failure":
      logger.error("password confirmation failed");
      break;
    case "tokenRefresh":
      logger.info("token refresh succeeded");
      break;
    case "tokenRefresh_failure":
      logger.error("token refresh failed");
      break;
    case "signOut":
      if (!__localSignOutCompleted) {
        try { themeBuilder().reset(); } catch(_) {}
        try { Crisp.chat.hide(); Crisp.setTokenId(); Crisp.session.reset(); window.CRISP_TOKEN_ID = null; } catch(_) {}
        try { localStorage.removeItem('ably_token'); } catch(_) {}
        try { store.dispatch({ type: SET_ABLY_TOKEN, payload: null }); } catch(_) {}
        try { clearObjectStore(config.system.IDB_NAME, config.system.IDB_STORE); } catch(_) {}
        try { store.dispatch({ type: CLEAR_ALL }); } catch(_) {}
      }
      logger.info("user signed out");
      break;
    default:
      logger.info("unknown");
  }
};

Hub.listen("auth", authListener);

const waitForCustomerId = async (userId, accountId, attempts = 5, delayMs = 250) => {
  for (let i = 0; i < attempts; i++) {
    try {
      await getUser(userId, accountId, true);
      const stateUser = store.getState().authentication.user;
      if (stateUser?.customer_id) return stateUser.customer_id;
    } catch (_) {}
    if (i < attempts - 1) await new Promise(r => setTimeout(r, delayMs));
  }
  return null;
};